import React, { useCallback, useEffect, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";

// 3D Secure screen — shows recent card-not-present challenges that the
// user's bank is asking them to approve. Pending rows get Approve / Reject
// buttons; resolved rows (APPROVED / REJECTED / EXPIRED) are shown for
// context but can no longer be acted on.
//
// Orenda clamps the look-back window to 72h. We default to 24h and bump
// to 72h on an explicit "show older" toggle so a user who missed one over
// the weekend can still find it.
//
// Pending rows carry a `requestExpiresAt` — we render a live countdown
// so the user can see how long they have. Once the window elapses the
// status will flip to EXPIRED on the next refresh.
//
// We quietly poll every 15s while the user has the screen open so a push
// from the bank shows up without a manual refresh. The poll stops the
// moment the view unmounts.

const POLL_MS = 15_000;

const parseListResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let raw = null;
  if (typeof rawStr === "string") {
    try { raw = JSON.parse(rawStr); } catch { raw = null; }
  }
  const typed = response?.payload?.data ?? response?.data ?? null;
  return (
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(typed?.data) && typed.data) ||
    (Array.isArray(typed) && typed) ||
    []
  );
};

const normaliseStatus = (status) => String(status || "").toUpperCase();

const STATUS_CHIP_CLASS = {
  PENDING: "orenda-pay-chip--pending",
  APPROVED: "orenda-pay-chip--ok",
  REJECTED: "orenda-pay-chip--err",
  EXPIRED: "orenda-pay-chip--neutral",
};
const chipClass = (status) =>
  STATUS_CHIP_CLASS[normaliseStatus(status)] || "orenda-pay-chip--neutral";

const formatStatus = (status) => {
  const s = normaliseStatus(status);
  if (!s) return "—";
  return s.charAt(0) + s.slice(1).toLowerCase();
};

const formatAmount = (amount, currency) => {
  if (amount == null) return "—";
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) return `${amount} ${currency || ""}`.trim();
  return `${n.toFixed(2)} ${currency || ""}`.trim();
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return ""; }
};

// Compact mm:ss / h m countdown between now and the given ISO timestamp.
// Returns null if the deadline has already passed (caller treats as expired).
const formatCountdown = (iso) => {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  if (diffMs <= 0) return null;
  const totalSec = Math.floor(diffMs / 1000);
  if (totalSec >= 3600) {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const Orenda3ds = () => {
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [list, setList] = useState([]);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(24); // 24 default; toggleable to 72
  const [actioningId, setActioningId] = useState(null); // row currently being approved/rejected
  const [actionError, setActionError] = useState(null);
  // Forces a re-render every second so countdowns tick. Cheap trick —
  // we only re-render while the view is mounted; polling is unaffected.
  const [, setNowTick] = useState(0);
  const pollRef = useRef(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    setError(null);
    setPhase(list.length ? "ready" : "loading");
    const id = ++requestIdRef.current;
    try {
      const resp = await orendaService.getRecent3dsNotificationsOrenda({ hours });
      if (id !== requestIdRef.current) return;
      setList(parseListResponse(resp));
      setPhase("ready");
    } catch (err) {
      if (id !== requestIdRef.current) return;
      setError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Couldn't load 3DS challenges."
      );
      setPhase("error");
    }
    // list.length kept out of deps on purpose — we only use it for the
    // initial "soft reload" heuristic above, re-reading current render is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  // Initial fetch + whenever the look-back window toggles.
  useEffect(() => { load(); }, [load]);

  // Background poll — stops when the view unmounts.
  useEffect(() => {
    pollRef.current = window.setInterval(() => { load(); }, POLL_MS);
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [load]);

  // Countdown ticker — 1s cadence while any PENDING row is on screen.
  useEffect(() => {
    const hasPending = list.some((r) => normaliseStatus(r.status) === "PENDING");
    if (!hasPending) return undefined;
    const t = window.setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [list]);

  const act = async (notif, action) => {
    if (!notif?.id) return;
    setActionError(null);
    setActioningId(notif.id);
    try {
      await orendaService.confirm3dsChallengeOrenda({
        authId: notif.id,
        action,
      });
      // Optimistically flip the local row so the UI reacts immediately,
      // then refetch to pick up any server-side status normalisation.
      setList((prev) =>
        prev.map((r) =>
          r.id === notif.id
            ? { ...r, status: action === "approve" ? "APPROVED" : "REJECTED" }
            : r
        )
      );
      setActioningId(null);
      load();
    } catch (err) {
      setActioningId(null);
      setActionError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Couldn't record your decision. Please try again."
      );
    }
  };

  const pendingCount = list.filter(
    (r) => normaliseStatus(r.status) === "PENDING"
  ).length;

  return (
    <section className="orenda-benes orenda-3ds-screen">
      <header className="orenda-benes-head">
        <div>
          <div className="orenda-eyebrow">3D Secure</div>
          <h1 className="orenda-benes-title">
            Approve a card purchase
            {pendingCount > 0 && (
              <span className="orenda-3ds-pending-badge">{pendingCount}</span>
            )}
          </h1>
          <p className="orenda-benes-sub">
            When a merchant asks your bank for an extra check, it shows up
            here. Approve the ones you recognise and reject anything that
            looks off.
          </p>
        </div>
        <div className="orenda-benes-segmented orenda-3ds-range">
          <button
            type="button"
            className={hours === 24 ? "is-active" : ""}
            onClick={() => setHours(24)}
          >
            Last 24h
          </button>
          <button
            type="button"
            className={hours === 72 ? "is-active" : ""}
            onClick={() => setHours(72)}
          >
            Last 72h
          </button>
        </div>
      </header>

      {phase === "loading" && (
        <div className="orenda-benes-card orenda-benes-empty">Loading…</div>
      )}

      {phase === "error" && (
        <div className="orenda-benes-card orenda-benes-empty">
          <p>{error}</p>
          <button
            type="button"
            className="orenda-btn-primary"
            onClick={load}
          >
            Try again
          </button>
        </div>
      )}

      {phase === "ready" && list.length === 0 && (
        <div className="orenda-benes-card orenda-benes-empty">
          <h3>Nothing to approve</h3>
          <p>
            When your bank asks for an extra check on a card purchase, it'll
            appear here. We check for new ones automatically.
          </p>
        </div>
      )}

      {phase === "ready" && list.length > 0 && (
        <ul className="orenda-benes-list orenda-3ds-list">
          {list.map((n) => {
            const status = normaliseStatus(n.status);
            const isPending = status === "PENDING";
            const countdown = isPending ? formatCountdown(n.requestExpiresAt) : null;
            const expired = isPending && countdown === null;
            return (
              <li
                key={n.id}
                className={`orenda-3ds-card${
                  isPending ? " orenda-3ds-card--pending" : ""
                }`}
              >
                <div className="orenda-3ds-row-top">
                  <div className="orenda-3ds-merchant">
                    <div className="orenda-3ds-merchant-name">
                      {n.merchantName || "Unknown merchant"}
                    </div>
                    <div className="orenda-3ds-merchant-meta">
                      {n.maskedPan && (
                        <span className="orenda-pay-ref">{n.maskedPan}</span>
                      )}
                      <span>{formatDateTime(n.created)}</span>
                    </div>
                  </div>
                  <div className="orenda-3ds-amount">
                    {formatAmount(n.amount, n.currency)}
                  </div>
                </div>

                <div className="orenda-3ds-row-bottom">
                  <div className="orenda-3ds-status">
                    <span className={`orenda-pay-chip ${chipClass(status)}`}>
                      {formatStatus(status)}
                    </span>
                    {isPending && !expired && countdown && (
                      <span className="orenda-3ds-countdown">
                        Expires in <strong>{countdown}</strong>
                      </span>
                    )}
                    {isPending && expired && (
                      <span className="orenda-3ds-countdown orenda-3ds-countdown--expired">
                        Window closed — refresh for status
                      </span>
                    )}
                  </div>

                  {isPending && !expired && (
                    <div className="orenda-3ds-actions">
                      <button
                        type="button"
                        className="orenda-btn-danger"
                        onClick={() => act(n, "reject")}
                        disabled={actioningId === n.id}
                      >
                        {actioningId === n.id ? "…" : "Reject"}
                      </button>
                      <button
                        type="button"
                        className="orenda-btn-primary"
                        onClick={() => act(n, "approve")}
                        disabled={actioningId === n.id}
                      >
                        {actioningId === n.id ? "…" : "Approve"}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {actionError && (
        <div
          className="orenda-benes-card orenda-benes-empty"
          style={{ color: "#c04040", marginTop: 12 }}
        >
          {actionError}
        </div>
      )}
    </section>
  );
};

export default Orenda3ds;
